import { useDropzone } from "react-dropzone";
import { FC, useCallback } from "react";
import { Upload, UploadCloud } from "lucide-react";
import { FILE_TYPE_MAP, getAcceptedMimeTypes } from "../helpers";
import { AllowedFileTypesEnum } from "@/api/lctApISchemas";
import { Button } from "@/components/ui/button";

interface UploadDropzoneProps {
  allowedFileTypes: AllowedFileTypesEnum[];
  onFilesAdded: (file: File) => void;
  disabled: boolean;
  hasError: boolean;
}

export const UploadDropzone: FC<UploadDropzoneProps> = ({ allowedFileTypes, onFilesAdded, disabled, hasError }) => {
  const accept = getAcceptedMimeTypes(allowedFileTypes);
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        // Upload files one by one
        for (const file of acceptedFiles) {
          onFilesAdded(file);
        }
      }
    },
    [onFilesAdded],
  );

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    accept,
    maxFiles: 5,
    maxSize: 10485760,
    disabled,
  });

  return (
    <div className={`w-full`}>
      <div>
        <div
          {...getRootProps()}
          className={`relative flex w-full border-spacing-2 cursor-pointer flex-col items-center justify-center rounded-sm border py-6 ${!disabled ? "cursor-pointer hover:bg-slate-100" : "cursor-no-drop"} ${hasError ? "border-dashed border-error bg-red-50" : "border-dashed border-slate-400 bg-slate-50 hover:border-slate-500 hover:bg-slate-100"}`}
        >
          <input {...getInputProps()} />
          <div className="text-center">
            <div className="mx-auto max-w-min">
              <UploadCloud size={22} />
            </div>

            <p className="m-4 text-sm text-gray-900">
              <span className="font-semibold">Drag and drop your files here</span>
            </p>
            <p className="mt-1 text-xs text-gray-700">
              Supported file types: {allowedFileTypes.map((type) => FILE_TYPE_MAP[type].label).join(", ")}
            </p>
            <p className="text-xs text-gray-700">{`Files should be under ${Math.floor(10485760 / (1024 * 1024))}MB each (max 5 files)`}</p>
            <Button
              variant="outline"
              disabled={disabled}
              className="mt-4 border-purple-300 bg-purple-100 text-purple-900 hover:bg-purple-100"
            >
              <Upload size={18} className="mr-2" /> Upload Files
            </Button>
          </div>
        </div>

        {fileRejections.length > 0 && (
          <div className="mt-2 text-sm text-error">
            {fileRejections.map(({ file, errors }) => (
              <div key={file.name}>
                {errors.map((error) => (
                  <p key={error.code}>{error.message}</p>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
